import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtDA } from "@/lib/format";
import { Plus, Search, Truck, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({
  societe_id: "", raison_sociale: "", nom_commercial: "", adresse: "", telephone: "",
  email: "", rc: "", nif: "", nis: "", contact_commercial: "", devise: "DA",
  conditions_paiement: "", delai_accorde: 0, coordonnees_bancaires: "", observations: "",
});

export default function Fournisseurs() {
  const { societes, societeParam, societeId } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fournisseurs", { params: { ...societeParam, search: search || undefined } });
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const f = emptyForm();
    if (societeId !== "all") f.societe_id = societeId;
    else if (societes.length === 1) f.societe_id = societes[0].id;
    setForm(f);
    setOpen(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.societe_id) return toast.error("Sélectionnez une société.");
    if (!form.raison_sociale) return toast.error("La raison sociale est requise.");
    setSaving(true);
    try {
      await api.post("/fournisseurs", { ...form, delai_accorde: Number(form.delai_accorde) || 0 });
      toast.success("Fournisseur créé.");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Fournisseurs"
        subtitle="Fiches fournisseurs et soldes à payer"
        testid="fournisseurs-page"
        actions={<Button onClick={openNew} className="gap-2" data-testid="new-fournisseur-btn"><Plus className="h-4 w-4" /> Nouveau fournisseur</Button>}
      />

      <div className="relative w-full sm:w-72 mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="fourn-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9" />
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <EmptyState icon={Truck} title="Aucun fournisseur" hint="Ajoutez vos fournisseurs pour suivre commandes, factures et paiements." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="fournisseurs-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Raison sociale</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Conditions</TableHead>
                  <TableHead className="text-right">Reste à payer</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((f) => (
                  <TableRow key={f.id} data-testid={`fourn-row-${f.id}`} className="cursor-pointer" onClick={() => navigate(`/fournisseurs/${f.id}`)}>
                    <TableCell>
                      <p className="font-medium">{f.raison_sociale}</p>
                      {f.nom_commercial && <p className="text-xs text-muted-foreground">{f.nom_commercial}</p>}
                    </TableCell>
                    <TableCell className="text-sm">{f.telephone || "—"}</TableCell>
                    <TableCell className="text-sm">{f.conditions_paiement || "—"}</TableCell>
                    <TableCell className={`tabular text-right font-semibold ${f.solde > 0 ? "text-destructive" : "text-muted-foreground"}`}>{fmtDA(f.solde)}</TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="fournisseur-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouveau fournisseur</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Société *</Label>
              <Select value={form.societe_id} onValueChange={(v) => setField("societe_id", v)}>
                <SelectTrigger data-testid="fourn-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Raison sociale *</Label><Input data-testid="fourn-raison" value={form.raison_sociale} onChange={(e) => setField("raison_sociale", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Nom commercial</Label><Input value={form.nom_commercial} onChange={(e) => setField("nom_commercial", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => setField("telephone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>E-mail</Label><Input value={form.email} onChange={(e) => setField("email", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => setField("adresse", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>RC</Label><Input value={form.rc} onChange={(e) => setField("rc", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>NIF</Label><Input value={form.nif} onChange={(e) => setField("nif", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>NIS</Label><Input value={form.nis} onChange={(e) => setField("nis", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Contact commercial</Label><Input value={form.contact_commercial} onChange={(e) => setField("contact_commercial", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Conditions de paiement</Label><Input value={form.conditions_paiement} onChange={(e) => setField("conditions_paiement", e.target.value)} placeholder="Ex: 30 jours" /></div>
            <div className="space-y-1.5"><Label>Délai accordé (jours)</Label><Input type="number" value={form.delai_accorde} onChange={(e) => setField("delai_accorde", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Coordonnées bancaires</Label><Input value={form.coordonnees_bancaires} onChange={(e) => setField("coordonnees_bancaires", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Observations</Label><Textarea rows={2} value={form.observations} onChange={(e) => setField("observations", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="fourn-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
